import dayjs from 'dayjs';
// @ts-expect-error dayjs-jalali is not recognized as an installed package. todo: checkout why and fix
import jalali from 'dayjs-jalali';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(jalali);
dayjs.tz.setDefault('Iran');

/**
 * Converts timestamp from microseconds to milliseconds if needed.
 * dayjs expects timestamps in milliseconds.
 * If timestamp is >= 1e13 (10 trillion), it's likely in microseconds and needs conversion.
 */
const normalizeTimestamp = (timestamp: number | string): number => {
  const ts = Number(timestamp);
  // If timestamp is >= 1e13 (10 trillion), it's likely in microseconds
  // Convert to milliseconds by dividing by 1000
  return ts >= 1e13 ? ts / 1000 : ts;
};

export const convertToIranTimeZone = (timestamp?: number | string) => {
  const getFaLocal = (ts?: number | string) => {
    if (!ts) return dayjs().locale('fa');
    const normalizedTs = normalizeTimestamp(ts);
    return dayjs(normalizedTs).locale('fa');
  };
  try {
    return timestamp ? getFaLocal(timestamp) : getFaLocal();
  } catch (e) {
    console.error('Error In Parsing Timestamp', e);
    if (!timestamp) return dayjs();
    const normalizedTs = normalizeTimestamp(timestamp);
    return dayjs(normalizedTs);
  }
};
/**
 * Formats a timestamp into a human-readable string based on its relation to the current date.
 * Supports Persian (Iran) timezone formatting and various display options.
 *
 * @param {string | undefined} timestamp - The timestamp to format (expected as a numeric string or undefined).
 * @param {boolean} [fullDate] - Optional flag to return the full date format regardless of time difference.
 * @returns {string} A formatted date string in Persian, varying based on the timestamp's proximity to now:
 * - Empty string if createdAt is undefined
 * - "امروز - HH:mm" if timestamp is today
 * - "دیروز - HH:mm" if timestamp is yesterday
 * - "dddd - HH:mm" if timestamp is in current week
 * - "dddd D MMMM YYYY - HH:mm" for full date or dates outside current week
 *
 * @example
 * // Assuming current date is 2025-03-01
 * getFormattedTimestamp("1709251200000") // Returns "جمعه 10 اسفند 1403 - 00:00"
 * getFormattedTimestamp(new Date().toString()) // Returns "امروز - HH:mm" (current time)
 * getFormattedTimestamp("1709251200000", true) // Returns "جمعه 10 اسفند 1403 - 00:00"
 */
// todo: refactor so it only gets format
export const getFormattedTimestamp = (
  timestamp: string | number | undefined,
  fullDate?: boolean,
  onlyTime?: boolean,
  format?: string,
) => {
  const now = dayjs();

  if (!timestamp) {
    return '';
  }

  const normalizedTimestamp = dayjs(normalizeTimestamp(timestamp));

  if (format) return convertToIranTimeZone(normalizedTimestamp.valueOf()).format(format);
  if (onlyTime) return convertToIranTimeZone(normalizedTimestamp.valueOf()).format('HH:mm');

  if (fullDate) return convertToIranTimeZone(normalizedTimestamp.valueOf()).format('dddd D MMMM YYYY - HH:mm');
  // Check if the timestamp is today
  const isToday = normalizedTimestamp.isSame(now, 'day');
  if (isToday) {
    return `امروز${convertToIranTimeZone(normalizedTimestamp.valueOf()).format(' - HH:mm')}`;
  }

  // Check if the timestamp is yesterday
  const isYesterday = normalizedTimestamp.isSame(now.subtract(1, 'day'), 'day');
  if (isYesterday) {
    return `دیروز${convertToIranTimeZone(normalizedTimestamp.valueOf()).format(' - HH:mm')}`;
  }

  // Check if the timestamp is within the current week
  const isThisWeek = normalizedTimestamp.isSame(now, 'week');
  if (isThisWeek) {
    return convertToIranTimeZone(normalizedTimestamp.valueOf()).format('dddd - HH:mm');
  }

  return convertToIranTimeZone(normalizedTimestamp.valueOf()).format('dddd D MMMM YYYY - HH:mm');
};

export const getPreviousTimestamp = (hoursAgo: number, serverTimeDiff?: number) => {
  return dayjs().subtract(hoursAgo, 'hours').valueOf() - (serverTimeDiff ?? 0);
};

export const getStartOfDayTimestamp = () => {
  return dayjs().startOf('day').valueOf();
};

/**
 *
 * @param hour - The hour to get the timestamp for.
 * @returns The timestamp for the given hour in this day with hour precision. if the current hour is earlier than the given hour, it will return the timestamp for the previous day.
 */
export const getHourOfTodayTimestamp = (hour: number) => {
  const now = dayjs();
  if (now.hour() < hour) {
    return now.subtract(1, 'day').hour(hour).startOf('hour').valueOf();
  }
  return now.hour(hour).startOf('hour').valueOf();
};
